/**
 * The PackedScene processor on the shared `createResourceProcessor` loop, exposed
 * as `ResourceLoader.scenes`. It is the PackedScene slice's loader-facing adapter
 * (`resources/formats/packedscene/`, ADR-0031), kept here because it loads without
 * `FileEventBus` and resolves ids through metadata.
 */
import { TscnParser } from '../../parser/TscnParser';
import { seatScene } from '../../parser/sceneTreeBuilder';
import { createResourceProcessor } from '../createResourceProcessor';
import { isGltfPath } from '../../godot/import/gltf';
import { fileBaseName } from '../../godot/string/string';
import { GLB_SCENE_ROOT_TYPE } from '../glbSceneRoot';
import { BINARY_RESOURCE_MAGIC, COMPRESSED_BINARY_RESOURCE_MAGIC, isBinaryScenePath, } from '../../godot/project/binaryResourceFormat';
/**
 * The `[gd_scene]` tag after any leading `;` comment and blank lines, which Godot
 * and the top-level parser both allow, so a scene that renders at top level also
 * loads when instanced. Anything else up front (HTML 404, stray text) fails. A comment
 * stops before `\r`, so each line has one reading and the match stays linear.
 */
const SCENE_HEADER = /^(?:[ \t]*(?:;[^\r\n]*)?\r?\n)*[ \t]*\[gd_scene/;
export function createSceneProcessor({ eventBus, resolveMetadata, getProvider, }) {
    const parser = new TscnParser();
    return createResourceProcessor({
        // No FileEventBus: the parser needs the path for relative-resource resolution,
        // so the host's `ResourceProvider` loads the scene directly inside `request()`'s
        // pre-emit hook, skipping the bytes-then-process round-trip.
        eventBus,
        resourceType: 'scene',
        loadDirectly: async (idOrPath) => {
            const metadata = resolveMetadata(idOrPath);
            if (!metadata) {
                throw new Error(`Scene metadata not found: ${idOrPath}`);
            }
            if (metadata.type && metadata.type !== 'PackedScene') {
                throw new Error(`Not a PackedScene resource: ${idOrPath} (type: ${metadata.type})`);
            }
            const provider = getProvider();
            if (!provider) {
                throw new Error('No ResourceProvider set');
            }
            // A HINT about what to fetch, not the claim the guard settled: the
            // channel it was asked through is what it is being asked for.
            const content = await provider.loadResource(metadata.path, metadata.type || 'PackedScene');
            if (content === null) {
                throw new Error(`Resource not found: ${metadata.path}`);
            }
            // A PackedScene can be a `.tscn` text file or a `.glb` / `.gltf` binary, and
            // one scene can reference both. The provider returns text or an ArrayBuffer,
            // so the registered path's extension decides how to make a TscnScene.
            if (isGltfPath(metadata.path)) {
                if (!(content instanceof ArrayBuffer)) {
                    throw new Error(`GLB/GLTF must be binary content, got ${typeof content}: ${metadata.path}`);
                }
                return synthesiseGLBScene(metadata.path);
            }
            // The lenient parser would "parse" a binary scene or non-TSCN text (an HTML 404)
            // into an empty scene whose subtree vanishes with no placeholder or
            // missing-resources row, so it fails instead.
            if (isBinaryScene(metadata.path, content)) {
                throw new Error(`Binary Godot scene (.scn, .res) is not previewable: only text scenes (.tscn) load: ${metadata.path}`);
            }
            if (typeof content !== 'string') {
                throw new Error(`TSCN scene must be text content: ${metadata.path}`);
            }
            if (!SCENE_HEADER.test(content)) {
                throw new Error(`Not a text scene (missing [gd_scene header): ${metadata.path}`);
            }
            return parser.parse(content);
        },
    });
}
/**
 * A `TscnScene` whose only root is a `GLBSceneRoot`, which loads the GLB through
 * `useResource('GLBMesh', path)`. Loading it here would block `'loaded'` on a
 * second fetch that no `invalidated` reaches, and the dispatcher gives
 * the GLB the same late-arrival, dispose and missing-resource paths as the rest.
 */
export function synthesiseGLBScene(glbPath) {
    // The file's base name, so the scene tree shows a meaningful label.
    const basename = fileBaseName(glbPath);
    const root = {
        // No file wrote the synthetic root, so it has no raw properties.
        rawProperties: {},
        name: basename || 'GLBRoot',
        type: GLB_SCENE_ROOT_TYPE,
        children: [],
        properties: { glbPath },
    };
    // Seated like a parsed root, so every `%Name` reader finds its (empty) claim table.
    const { roots } = seatScene([{ node: root, line: 0, declaredParent: undefined, uniqueId: 0 }]);
    return {
        nodes: roots,
        externalResources: [],
        internalResources: [],
        refusesToInstantiate: false,
    };
}
/** A scene request asks for a PackedScene, so its path decides, or a binary file's magic. */
function isBinaryScene(path, content) {
    if (isBinaryScenePath(path, 'PackedScene'))
        return true;
    return (typeof content === 'string' &&
        (content.startsWith(BINARY_RESOURCE_MAGIC) || content.startsWith(COMPRESSED_BINARY_RESOURCE_MAGIC)));
}
//# sourceMappingURL=createSceneProcessor.js.map